import React from 'react';
import type { JSONContent } from '@tiptap/react';
import { MessageSquare, Plus, Users } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import type { ChatMessageEditorHandle } from '../ui/ChatMessageEditor';
import { MessagingSplitView } from '../Messaging/MessagingSplitView';
import type { ThreadSummary } from '../Messaging/MessageThreadList';
import type { ThreadMessage, ThreadRead } from '../Messaging/ConversationPanel';

// Tiptap chargé à la demande uniquement — voir le même commentaire dans ConversationPanel.tsx.
const ChatMessageEditor = React.lazy(() => import('../ui/ChatMessageEditor').then((m) => ({ default: m.ChatMessageEditor })));

interface EmployeeOption {
    id: string;
    first_name: string;
    last_name: string;
}

const employeeName = (r: { first_name?: string; last_name?: string }) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Salarié';

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const TeamMessagingSettings: React.FC<{ employees: EmployeeOption[] }> = ({ employees }) => {
    const [threads, setThreads] = React.useState<ThreadSummary[]>([]);
    const [showCompose, setShowCompose] = React.useState(false);
    const [composeTitle, setComposeTitle] = React.useState('');
    const [composeTargets, setComposeTargets] = React.useState<string[]>([]);
    const [composeSearchTerm, setComposeSearchTerm] = React.useState('');
    const [composeAllowReplies, setComposeAllowReplies] = React.useState(true);
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState('');
    const composeEditorRef = React.useRef<ChatMessageEditorHandle>(null);

    const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(null);
    const [threadMessages, setThreadMessages] = React.useState<ThreadMessage[]>([]);
    const [threadLoading, setThreadLoading] = React.useState(false);
    const [replying, setReplying] = React.useState(false);
    const [viewingAttachmentId, setViewingAttachmentId] = React.useState<string | null>(null);
    const [otherReads, setOtherReads] = React.useState<ThreadRead[]>([]);
    const [recipientCount, setRecipientCount] = React.useState(1);
    const [allowReplies, setAllowReplies] = React.useState(true);

    // window.open() doit s'exécuter de façon SYNCHRONE au clic (avant tout await) sous peine d'être
    // bloqué silencieusement par le navigateur (geste utilisateur perdu après le fetch).
    const viewAttachment = async (messageId: string, win: Window | null) => {
        setViewingAttachmentId(messageId);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/team-announcement-messages/${messageId}/attachment`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); } finally {
            setViewingAttachmentId(null);
        }
    };

    const load = () => {
        moduleApi.getTeamAnnouncements().then((d: any) => setThreads(Array.isArray(d) ? d : [])).catch(() => setThreads([]));
    };
    React.useEffect(() => { load(); }, []);

    const openThread = async (id: string) => {
        setSelectedThreadId(id);
        setThreadLoading(true);
        try {
            const res: any = await moduleApi.getTeamAnnouncement(id);
            setThreadMessages(Array.isArray(res?.messages) ? res.messages : []);
            setOtherReads(Array.isArray(res?.other_reads) ? res.other_reads : []);
            setRecipientCount(res?.recipient_count || 1);
            setAllowReplies(res?.thread?.allow_replies !== 0);
        } catch {
            setThreadMessages([]);
        } finally {
            setThreadLoading(false);
            load();
        }
    };

    const handleCompose = async () => {
        const { json, text } = composeEditorRef.current?.getContent() || { json: {} as JSONContent, text: '' };
        if (!composeTitle.trim() || !text.trim()) { setError('Titre et message requis.'); return; }
        setSending(true);
        setError('');
        try {
            const res: any = await moduleApi.createTeamAnnouncement({ title: composeTitle.trim(), message: text.trim(), message_rich: json, target_employe_ids: composeTargets.length > 0 ? composeTargets : null, allow_replies: composeAllowReplies });
            if (res?.error) { setError(res.error); return; }
            setShowCompose(false);
            setComposeTitle(''); setComposeTargets([]); setComposeSearchTerm(''); setComposeAllowReplies(true);
            composeEditorRef.current?.clear();
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur');
        } finally {
            setSending(false);
        }
    };

    const handleReply = async ({ text, rich, attachment }: { text: string; rich: JSONContent; attachment: File | null }) => {
        if (!selectedThreadId) return;
        setReplying(true);
        try {
            const attachmentPayload = attachment ? { display_name: attachment.name, file_name: attachment.name, mime_type: attachment.type, data: await fileToBase64(attachment) } : undefined;
            const res: any = await moduleApi.replyTeamAnnouncement(selectedThreadId, text, rich, attachmentPayload);
            if (res?.error) { alert(res.error); return; }
            const detail: any = await moduleApi.getTeamAnnouncement(selectedThreadId);
            setThreadMessages(Array.isArray(detail?.messages) ? detail.messages : []);
            setOtherReads(Array.isArray(detail?.other_reads) ? detail.other_reads : []);
            setRecipientCount(detail?.recipient_count || 1);
            load();
        } finally {
            setReplying(false);
        }
    };

    const handleToggleAllowReplies = async (next: boolean) => {
        if (!selectedThreadId) return;
        setAllowReplies(next);
        try {
            await moduleApi.setTeamAnnouncementAllowReplies(selectedThreadId, next);
        } catch {
            setAllowReplies(!next);
        }
    };

    const selectedThread = threads.find((t) => t.id === selectedThreadId);

    return (
        <div className="space-y-4">
            <p className="text-xs text-content-muted italic">Annonces envoyées à vos salariés (diffusion ou ciblées), avec réponses.</p>

            <MessagingSplitView
                threads={threads}
                selectedThreadId={selectedThreadId}
                onSelectThread={openThread}
                subtitle={(t) => t.target_employe_id ? employeeName(employees.find((e) => e.id === t.target_employe_id) || {}) : <><Users size={11} /> Tous les salariés</>}
                emptyListLabel="Aucun message envoyé pour l'instant."
                headerExtra={<Button size="sm" onClick={() => setShowCompose(true)} className="w-full"><Plus size={14} /> Nouveau message</Button>}
                conversationTitle={selectedThread?.title || ''}
                conversationSubtitle={selectedThread?.target_employe_id ? employeeName(employees.find((e) => e.id === selectedThread.target_employe_id) || {}) : 'Tous les salariés'}
                messages={threadMessages}
                threadLoading={threadLoading}
                myAuthorType="admin"
                onReply={handleReply}
                sending={replying}
                onViewAttachment={viewAttachment}
                viewingAttachmentId={viewingAttachmentId}
                emptyConversationLabel="Sélectionnez une conversation ou envoyez un nouveau message."
                otherReads={otherReads}
                recipientCount={recipientCount}
                allowReplies={allowReplies}
                onToggleAllowReplies={handleToggleAllowReplies}
            />

            <Modal open={showCompose} onClose={() => setShowCompose(false)} title="Nouveau message" icon={<MessageSquare size={16} className="text-brand-500" />}
                maxWidth="max-w-sm"
                footer={<>
                    <Button variant="secondary" onClick={() => setShowCompose(false)}>Annuler</Button>
                    <Button onClick={handleCompose} disabled={sending}>{sending ? 'Envoi...' : 'Envoyer'}</Button>
                </>}>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Destinataires</label>
                        <div className="space-y-2">
                            <Input
                                value={composeSearchTerm}
                                onChange={(e) => setComposeSearchTerm(e.target.value)}
                                placeholder="Rechercher un salarié..."
                            />
                            {composeTargets.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {composeTargets.map((targetId) => {
                                        const emp = employees.find((e) => e.id === targetId);
                                        return emp ? (
                                            <button
                                                key={targetId}
                                                onClick={() => setComposeTargets((prev) => prev.filter((id) => id !== targetId))}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-600/20 border border-brand-600/50 text-xs font-semibold text-brand-600 hover:bg-brand-600/30 transition-colors"
                                            >
                                                {employeeName(emp)}
                                                <span className="text-xs font-bold">×</span>
                                            </button>
                                        ) : null;
                                    })}
                                </div>
                            )}
                            {composeSearchTerm.trim() && (
                                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                    {employees
                                        .filter((e) => {
                                            const name = employeeName(e).toLowerCase();
                                            return name.includes(composeSearchTerm.toLowerCase()) && !composeTargets.includes(e.id);
                                        })
                                        .map((e) => (
                                            <button
                                                key={e.id}
                                                type="button"
                                                onClick={() => setComposeTargets((prev) => [...prev, e.id])}
                                                className="w-full text-left px-3 py-2 rounded-lg bg-surface-soft border border-border hover:border-brand-500/40 text-sm text-content transition-colors"
                                            >
                                                {employeeName(e)}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Titre</label>
                        <Input value={composeTitle} onChange={(e) => setComposeTitle(e.target.value)} placeholder="Ex: Fermeture exceptionnelle" />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                        <React.Suspense fallback={<div className="h-[72px] rounded-xl border border-border bg-surface-soft animate-pulse" />}>
                            <ChatMessageEditor ref={composeEditorRef} placeholder="Écrivez votre message…" />
                        </React.Suspense>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-soft border border-border">
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-content">Permettre le dialogue</p>
                            <p className="text-[10px] text-content-muted mt-0.5">Si désactivé, le(s) salarié(s) ne pourront pas répondre.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input type="checkbox" checked={composeAllowReplies} className="sr-only peer"
                                onChange={(e) => setComposeAllowReplies(e.target.checked)} />
                            <div className="w-10 h-6 bg-surface rounded-full border border-border peer-checked:bg-brand-600 transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                        </label>
                    </div>
                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                </div>
            </Modal>
        </div>
    );
};

export default TeamMessagingSettings;
